import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { forgotPassword, clearError } from '../store/slices/authSlice';
import { FiMail, FiArrowLeft, FiCheckCircle } from 'react-icons/fi';
import { Helmet } from 'react-helmet-async';

const schema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
});

export const ForgotPasswordPage = () => {
  const [emailSent, setEmailSent] = useState(false);
  const dispatch = useDispatch();
  const { isLoading, error } = useSelector((state) => state.auth);

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const onSubmit = async (data) => {
    const result = await dispatch(forgotPassword(data.email));
    if (forgotPassword.fulfilled.match(result)) {
      setEmailSent(true);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <>
      <Helmet>
        <title>Forgot Password | Nitro Logs</title>
      </Helmet>

      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background Decorations */}
        <div className="absolute inset-0 luxury-dot-pattern opacity-30" />
        <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-primary-50 to-transparent" />
        
        {/* Floating Shapes */}
        <motion.div
          animate={{ 
            y: [0, -20, 0],
            rotate: [0, 5, 0]
          }}
          transition={{ duration: 8, repeat: Infinity }}
          className="absolute top-20 right-20 w-64 h-64 bg-primary-200 rounded-full blur-3xl opacity-20"
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full relative z-10"
        >
          {/* Back to Login */}
          <motion.div variants={fadeInUp} className="mb-8">
            <Link
              to="/login"
              className="inline-flex items-center space-x-2 text-luxury-600 hover:text-primary-600 transition-colors"
            >
              <FiArrowLeft />
              <span>Back to Login</span>
            </Link>
          </motion.div>

          {/* Header */}
          <motion.div variants={fadeInUp} className="text-center mb-8">
            <h2 className="text-3xl font-display font-bold text-luxury-900 mb-2">
              Forgot Password?
            </h2>
            <p className="text-luxury-600">
              Enter your email and we'll send you instructions to reset your password
            </p>
          </motion.div>

          {/* Main Card */}
          <motion.div
            variants={fadeInUp}
            className="glass border border-luxury-200 rounded-2xl p-8 shadow-luxury-xl"
          >
            {emailSent ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center"
              >
                <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
                  <FiCheckCircle className="w-10 h-10 text-green-500" />
                </div>
                <h3 className="text-xl font-semibold text-luxury-900 mb-2">
                  Check Your Email
                </h3>
                <p className="text-luxury-600 mb-6">
                  We've sent password reset instructions to your email address.
                </p>
                <Link
                  to="/login"
                  className="inline-block luxury-button"
                >
                  Return to Login
                </Link>
              </motion.div>
            ) : (
              <>
                {/* Error Message */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm"
                  >
                    {error.message || 'Failed to send reset email. Please try again.'}
                  </motion.div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  {/* Email Field */}
                  <div>
                    <label className="block text-sm font-medium text-luxury-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <FiMail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-luxury-400" />
                      <input
                        type="email"
                        {...register('email')}
                        className={`input-luxury pl-12 ${errors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                        placeholder="you@example.com"
                      />
                    </div>
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <motion.button
                    type="submit"
                    disabled={isLoading}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full luxury-button disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Sending...
                      </div>
                    ) : (
                      'Send Reset Instructions'
                    )}
                  </motion.button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      </div>
    </>
  );
};